import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    quote: "Ich hab' Plottie als Geschenk für meinen dreijährigen Sohn ausprobiert. Seine Augen leuchten jedes Mal, wenn er seinen Namen in den Geschichten hört… Und seinen Teddybär.",
    author: "Simon K., Papa von Karl (2)",
    location: "Euskirchen",
    rating: 5,
    image: "/Mom-1.png"
  },
  {
    id: 2,
    quote: "Was ich besonders toll finde, sind die verschiedenen Erzählstimmen, die man auswählen kann. So finde ich immer eine, die zu unseren Geschichten passt.",
    author: "Sarah M., Mama von Lina (2) & Noah (4)",
    location: "Hamburg",
    rating: 4.5,
    image: "/Mom-2.png"
  },
  {
    id: 3,
    quote: "Ist echt einfach… Und jedes Mal irgendwie besonders. Leon freut sich immer schon auf neue Geschichten.",
    author: "Eliza E., Mama von Leon (3)",
    location: "Oldenburg",
    rating: 5,
    image: "/Mom-3.png"
  }
];

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isAnimatingMobile, setIsAnimatingMobile] = useState(false);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkIsDesktop();
    window.addEventListener('resize', checkIsDesktop);
    return () => window.removeEventListener('resize', checkIsDesktop);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isDesktop && isAnimatingMobile) return;
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [isDesktop, isAnimatingMobile]);

  const desktopTransitionConfig = {
    type: "spring",
    stiffness: 200,
    damping: 28,
    mass: 1,
    opacity: { duration: 0.35, ease: "easeInOut" }
  };

  const slideVariants = {
    enter: (customDirection) => ({ x: customDirection > 0 ? 500 : -500, opacity: 0 }),
    center: { zIndex: 1, x: 0, opacity: 1 },
    exit: (customDirection) => ({ zIndex: 0, x: customDirection < 0 ? 500 : -500, opacity: 0 })
  };

  const mobileTransition = {
    x: { type: "spring", stiffness: 200, damping: 25, mass: 1.2 },
    opacity: { duration: 0.4, ease: "easeInOut" }
  };

  const handleDragEnd = (event, info) => {
    const swipeThreshold = 50;
    const dragDistance = info.offset.x;
    if (Math.abs(dragDistance) > swipeThreshold) {
      if (dragDistance > 0) {
        setDirection(-1);
        setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
      } else {
        setDirection(1);
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }
    }
  };
  
  const handlePaginationClick = (index) => {
    if (index === currentIndex) return;
    if (!isDesktop && isAnimatingMobile) return;
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  }

  const getDesktopItemStyle = (itemIndex, activeIndex, totalItems) => {
    const X_TRANSLATE_ADJ = 90;
    const OPACITY_SIDE_ADJ = 0.3;
    let targetStyle = { x: "0%", scale: 1, opacity: 0, zIndex: 0, display: 'none' };

    const isCenter = itemIndex === activeIndex;
    const isPrevOfActive = itemIndex === (activeIndex - 1 + totalItems) % totalItems;
    const isNextOfActive = itemIndex === (activeIndex + 1) % totalItems;

    if (isCenter) {
      targetStyle = { x: "0%", scale: 1, opacity: 1, zIndex: 2, display: 'flex' };
    } else if (isPrevOfActive) {
      targetStyle = { x: `-${X_TRANSLATE_ADJ}%`, scale: 1, opacity: OPACITY_SIDE_ADJ, zIndex: 1, display: 'flex' };
    } else if (isNextOfActive) {
      targetStyle = { x: `${X_TRANSLATE_ADJ}%`, scale: 1, opacity: OPACITY_SIDE_ADJ, zIndex: 1, display: 'flex' };
    } else {
      targetStyle = { x: itemIndex < activeIndex ? "-200%" : "200%", scale: 1, opacity: 0, zIndex: 0, display: 'none' };
    }

    return targetStyle;
  };

  const currentTestimonial = testimonials[currentIndex];

  const renderTestimonialCardContent = (testimonial) => (
    <>
      <div className="w-full md:w-1/3 flex-shrink-0 flex justify-center">
        <img
          src={testimonial.image}
          alt={testimonial.author}
          className="w-full h-60 md:h-80 object-contain pointer-events-none"
          draggable="false"
        />
      </div>
      <div className="w-full md:w-1/2 flex flex-col justify-center text-center md:text-left px-4 md:px-0">
        <div className="flex gap-1 mb-4 justify-center md:justify-start items-center">
          {[...Array(5)].map((_, i) => {
            const ratingValue = testimonial.rating;
            return (
              <Star
                key={i}
                className={`w-4 h-4 md:w-6 md:h-6 ${
                  i + 1 <= ratingValue
                    ? 'text-[#FFA23E] fill-[#FFA23E]'
                    : i + 0.5 <= ratingValue
                    ? 'text-[#FFA23E] fill-[#FFA23E] [clip-path:inset(0_50%_0_0)]'
                    : 'text-[#FFA23E] fill-none'
                }`}
              />
            );
          })}
          <span className="ml-2 text-[#343434] pt-1 text-sm md:text-base font-aleo">
            {testimonial.rating} von 5 Sternen
          </span>
        </div>
        <blockquote className="text-[#343434] text-base md:text-[1.25rem] font-normal leading-[150%] font-aleo mb-6">
          "{testimonial.quote}"
        </blockquote>
        <div className="font-freeman">
          <p className="font-freeman text-[#343434] text-lg font-normal leading-[110%] tracking-[-0.0375rem]">
            {testimonial.author}
          </p>
          <p className="text-[#343434] text-base">
            {testimonial.location}
          </p>
        </div>
      </div>
    </>
  );

  return (
    <section className="w-full bg-[#EDD1BA] py-12 md:py-40">
      <div className="container mx-auto px-4 max-w-6xl">
        <h2 className="font-freeman text-3xl md:text-4xl text-[#343434] tracking-[-0.06375rem] text-center mb-4 md:mb-12">
          Das erzählen Eltern über Plottie
        </h2>

        {isDesktop ? (
          <motion.div
            className="relative h-[580px] md:h-[400px] flex items-center cursor-grab active:cursor-grabbing"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.7}
            onDragEnd={handleDragEnd}
          >
            {testimonials.map((testimonial, index) => {
              const animateStyle = getDesktopItemStyle(
                index,
                currentIndex,
                testimonials.length
              );
              return (
                <motion.div
                  key={testimonial.id}
                  className="absolute w-full h-full flex"
                  style={{ transformOrigin: 'center center', touchAction: 'pan-y' }}
                  initial={false}
                  animate={animateStyle}
                  transition={desktopTransitionConfig}
                >
                  <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 w-full h-full">
                    {renderTestimonialCardContent(testimonial)}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <div className="relative h-[560px] md:h-[400px]">
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={mobileTransition}
                onAnimationStart={() => setIsAnimatingMobile(true)}
                onAnimationComplete={() => setIsAnimatingMobile(false)}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={1}
                onDragEnd={handleDragEnd}
                className="absolute w-full h-full touch-pan-y"
              >
                <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 h-full">
                  {renderTestimonialCardContent(currentTestimonial)}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        <div className="flex justify-center gap-2">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => handlePaginationClick(index)}
              className={`w-2 h-2 rounded-full transition-colors duration-300 hover:bg-[#343434]/50 ${
                index === currentIndex ? 'bg-[#343434]' : 'bg-[#343434]/20'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials; 